import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApiClient } from "../api/ApiClientContext";
import { AnnotationsApi } from "../api/annotations";
import { FrameThumb } from "../components/FrameThumb";
import { FrameLightbox } from "../components/FrameLightbox";
import { ExportFramesButton } from "../components/ExportFramesButton";
import { frameDownloadName } from "../export/frameFilename";
import { useAuth } from "../auth/AuthContext";
import { useTourAutoStart } from "../tour/useTourAutoStart";
import { applyFrameFilter, type FilterMode } from "../frames/frameFilter";
import { runPoiCapture } from "../frames/runPoiCapture";
import { poisWithoutFrame } from "../frames/poiCapture";
import type { Annotations, Frame } from "../api/types";

export function FramesPage(): JSX.Element {
  const { id: flightId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const teamParam = searchParams.get("team") ?? undefined;
  const focusId = searchParams.get("focus");
  const teamSuffix = teamParam !== undefined ? `?team=${encodeURIComponent(teamParam)}` : "";
  const apiClient = useApiClient();
  const annotationsApi = useMemo(() => new AnnotationsApi(apiClient), [apiClient]);
  const navigate = useNavigate();
  const { team: ownTeam, reviewerId: ownReviewerId, token, displayName } = useAuth();
  const isAdmin = ownTeam?.role === "admin";
  // A parent in a sub-client's scope has master (admin-equivalent) access —
  // see ViewerPage. Learned from the flight detail, which is fetched anyway.
  const foreignScope =
    teamParam !== undefined && ownTeam !== null && teamParam !== ownTeam.slug;
  const [master, setMaster] = useState(false);
  const canManageAll = isAdmin || (foreignScope && master);

  const jumpToVideo = (videoTimeS: number): void => {
    const sep = teamSuffix === "" ? "?" : "&";
    navigate(`/flights/${flightId}${teamSuffix}${sep}t=${videoTimeS}`);
  };

  const jumpToPano = (f: Frame): void => {
    if (f.panoramaId === undefined) return;
    // panoView is always set for panorama frames per the schema; the fallback
    // only guards persisted legacy/corrupt data.
    const pv = f.panoView ?? { yaw: 0, pitch: 0, zoom: 0 };
    const sep = teamSuffix === "" ? "?" : "&";
    navigate(
      `/flights/${flightId}${teamSuffix}${sep}pano=${encodeURIComponent(f.panoramaId)}` +
        `&yaw=${pv.yaw}&pitch=${pv.pitch}&zoom=${pv.zoom}`,
    );
  };

  const [annotations, setAnnotations] = useState<Annotations | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterMode>("all");
  const [activeFrameId, setActiveFrameId] = useState<string | null>(null);
  // thumbUrls: presigned URL, the literal "missing" sentinel, or undefined while loading.
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  const [activeFullUrl, setActiveFullUrl] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  // Flight display timezone, used to name downloads the same way the viewer's
  // capture path does (frame_HH_MM_SS.jpg). Best-effort: falls back to the
  // runtime's local zone if the flight fetch fails.
  const [tz, setTz] = useState<string | undefined>(undefined);

  // True once the annotations fetch has settled either way; an empty gallery
  // keeps `annotations` null, so the fetch result alone cannot signal readiness.
  const [loaded, setLoaded] = useState(false);
  useTourAutoStart("frames", loaded);

  const myReviewerId = ownReviewerId ?? "";

  // The filter buttons stay live during a capture (by design). A capture in
  // flight closes over its own `run`; without this ref it would export
  // whatever filter was active when "Save and export" was pressed, not the
  // one showing when the capture actually finishes.
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const isOwnOrAdmin = (frame: Frame): boolean =>
    frame.capturedBy.reviewerId === myReviewerId || canManageAll;

  useEffect(() => {
    if (flightId === undefined) return;
    let cancelled = false;
    (async () => {
      try {
        const ann = await annotationsApi.get(flightId, teamParam);
        if (cancelled) return;
        setAnnotations(ann.annotations);
        setLoaded(true);
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [flightId, annotationsApi, teamParam, reloadTick]);

  useEffect(() => {
    if (flightId === undefined) return;
    let cancelled = false;
    apiClient
      .getFlight(flightId, { team: teamParam })
      .then((d) => {
        if (cancelled) return;
        setTz(d.flight.displayTimezone);
        setMaster(d.access?.master === true);
      })
      .catch(() => { /* best-effort; download name falls back to local tz */ });
    return () => { cancelled = true; };
  }, [flightId, apiClient, teamParam]);

  // Resolve thumbnail URLs for each frame. Skip frames where hasThumb===false:
  // for those we render a "thumbnail missing" placeholder via FrameThumb's
  // sentinel src="missing" path.
  useEffect(() => {
    if (flightId === undefined) return;
    const frames = annotations?.frames ?? [];
    if (frames.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const f of frames) {
        if (cancelled) return;
        if (thumbUrls[f.id] !== undefined) continue;
        if (f.hasThumb === false) {
          setThumbUrls((prev) => ({ ...prev, [f.id]: "missing" }));
          continue;
        }
        try {
          const url = await annotationsApi.frameUrl(flightId, f.id, "thumb", teamParam);
          if (cancelled) return;
          setThumbUrls((prev) => ({ ...prev, [f.id]: url }));
        } catch {
          // Skip; thumbnail will stay broken.
        }
      }
    })();
    return () => { cancelled = true; };
  }, [annotations, flightId, annotationsApi, thumbUrls, teamParam]);

  // Resolve the full-res URL when a frame is opened in the lightbox.
  useEffect(() => {
    if (flightId === undefined || activeFrameId === null) {
      setActiveFullUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = await annotationsApi.frameUrl(flightId, activeFrameId, "full", teamParam);
        if (!cancelled) setActiveFullUrl(url);
      } catch {
        if (!cancelled) setActiveFullUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [activeFrameId, flightId, annotationsApi, teamParam]);

  // Open lightbox when ?focus= matches a loaded frame.
  useEffect(() => {
    if (!focusId) return;
    const frames = annotations?.frames ?? [];
    const target = frames.find((f) => f.id === focusId);
    if (target) setActiveFrameId(target.id);
  }, [focusId, annotations]);

  if (flightId === undefined) return <div>Missing flight id</div>;
  if (error !== null) return <div role="alert">Error: {error}</div>;

  const frames: Frame[] = annotations?.frames ?? [];
  const filtered = applyFrameFilter(frames, filter, myReviewerId);

  const active = frames.find((f) => f.id === activeFrameId) ?? null;

  const missingPois = poisWithoutFrame(annotations);
  const capture =
    filter !== "broken" && missingPois.length > 0 && myReviewerId !== ""
      ? {
          missingCount: missingPois.length,
          run: async (o: {
            signal: AbortSignal;
            onProgress: (done: number, total: number) => void;
          }) => {
            let failed = 0;
            try {
              const res = await runPoiCapture({
                apiClient,
                annotationsApi,
                flightId,
                team: teamParam,
                reviewer: { reviewerId: myReviewerId, displayName: displayName ?? "" },
                authToken: token ?? undefined,
                workerOrigin: import.meta.env.VITE_API_URL ?? "http://localhost:8787",
                signal: o.signal,
                onProgress: o.onProgress,
              });
              failed = res.failed;
            } catch (err) {
              // A run that died part-way may still have saved frames: show them.
              setReloadTick((n) => n + 1);
              throw err;
            }
            const fresh = await annotationsApi.get(flightId, teamParam);
            setAnnotations(fresh.annotations);
            return {
              failed,
              frames: applyFrameFilter(
                fresh.annotations?.frames ?? [], filterRef.current, myReviewerId,
              ),
              pois: fresh.annotations?.pois ?? [],
            };
          },
        }
      : undefined;

  const handleDeleteFrame = async (frameId: string): Promise<void> => {
    const frame = frames.find((f) => f.id === frameId);
    if (frame !== undefined && !isOwnOrAdmin(frame)) {
      alert(`You don't have permission to delete this frame. Owner: ${frame.capturedBy.displayName}`);
      return;
    }
    if (!confirm("Delete this frame?")) return;
    try {
      await annotationsApi.deleteFrame(flightId, frameId, teamParam, myReviewerId);
    } catch (err) {
      alert(`Delete failed: ${(err as Error).message}`);
      return;
    }
    // The worker DELETE only removes the R2 blobs; the frame entry still
    // lives in annotations.json. Strip it here with an If-Match PUT so the
    // gallery actually shrinks. Retry once on 412 (conflict from a concurrent
    // editor). On any error we still flip local UI optimistically — the
    // R2 blobs are gone either way.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const fresh = await annotationsApi.get(flightId, teamParam).catch(() => null);
      if (fresh?.annotations === null || fresh === null) break;
      const next: Annotations = {
        ...fresh.annotations,
        frames: fresh.annotations.frames.filter((f) => f.id !== frameId),
        updatedAt: new Date().toISOString(),
      };
      let result: { kind: string } | undefined;
      try {
        result = await annotationsApi.put(flightId, next, fresh.etag, teamParam);
      } catch {
        result = { kind: "error" };
      }
      if (!result || result.kind !== "conflict") break;
      // conflict → loop and retry once with fresh etag
    }
    setActiveFrameId(null);
    setThumbUrls((prev) => {
      const next = { ...prev };
      delete next[frameId];
      return next;
    });
    setReloadTick((n) => n + 1);
  };

  return (
    <div style={{ padding: 12 }} data-testid="frames-page" data-focus-id={focusId ?? ""}>
      <header style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <Link
          to={`/flights/${flightId}${teamSuffix}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: "#1a1a1a",
            color: "#eee",
            border: "1px solid #333",
            borderRadius: 4,
            textDecoration: "none",
            fontSize: 14,
            lineHeight: 1,
          }}
        >
          ← Back to map
        </Link>
        <h2 data-tour="frames.heading" style={{ flex: 1 }}>
          Saved Frames ({frames.length})
        </h2>
        <span data-tour="frames.filters" style={{ display: "inline-flex", gap: 6 }}>
          {(["all", "mine", "linked", "broken"] as FilterMode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setFilter(m)}
              disabled={filter === m}
              style={{ padding: "4px 10px" }}
            >
              {m}
            </button>
          ))}
        </span>
        <ExportFramesButton
          flightId={flightId}
          frames={filtered}
          pois={annotations?.pois ?? []}
          filterLabel={filter}
          tz={tz}
          frameUrl={(frameId) => annotationsApi.frameUrl(flightId, frameId, "full", teamParam)}
          capture={capture}
        />
      </header>
      {frames.length === 0 ? (
        <p>No frames yet. Use 📷 in the viewer to capture one.</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {filtered.map((f) => {
            const thumbState = thumbUrls[f.id];
            const src: string | null =
              thumbState === undefined ? null : thumbState; // "missing" passes through
            const captionPrefix = `${f.capturedBy.displayName} · ${new Date(f.capturedAt).toLocaleString()}`;
            const caption = f.hasThumb === false
              ? `${captionPrefix} — thumbnail missing, delete & recapture`
              : captionPrefix;
            const isPano = f.source === "panorama";
            return (
              // width:320 matches the thumbnail so the absolutely-positioned
              // overlays (jump button, 360 badge, actions menu) anchor to the
              // image edges rather than the wider stretched grid cell.
              <div key={f.id} style={{ position: "relative", width: 320 }}>
                <FrameThumb
                  src={src}
                  alt={`frame ${f.id}`}
                  caption={caption}
                  onClick={() => setActiveFrameId(f.id)}
                />
                {isPano && (
                  <span
                    style={{
                      position: "absolute",
                      // Bottom-right corner of the 320x180 thumbnail image,
                      // clear of the caption that sits below the image.
                      top: 156,
                      right: 4,
                      background: "rgba(0,0,0,0.7)",
                      color: "#fff",
                      borderRadius: 4,
                      padding: "2px 6px",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    360°
                  </span>
                )}
                <button
                  type="button"
                  data-tour="frames.jump"
                  onClick={() => (isPano ? jumpToPano(f) : jumpToVideo(f.videoTimeS))}
                  aria-label={
                    isPano
                      ? "Jump to panorama"
                      : `Jump to video at ${f.videoTimeS.toFixed(1)}s`
                  }
                  style={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    background: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  {isPano ? "🌐 Jump to panorama" : "⏯ Jump to video"}
                </button>
                <details
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                  }}
                >
                    <summary
                      aria-label="Frame actions"
                      data-tour="frames.actions"
                      style={{
                        listStyle: "none",
                        cursor: "pointer",
                        background: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        width: 28,
                        height: 28,
                        borderRadius: 14,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        lineHeight: 1,
                      }}
                    >
                      ⋯
                    </summary>
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        marginTop: 4,
                        background: "#fff",
                        color: "#000",
                        border: "1px solid #ccc",
                        borderRadius: 4,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                        minWidth: 100,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => { void handleDeleteFrame(f.id); }}
                        style={{
                          display: "block",
                          width: "100%",
                          padding: "6px 12px",
                          border: "none",
                          background: "transparent",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </details>
              </div>
            );
          })}
        </div>
      )}
      {active !== null && (
        <FrameLightbox
          frame={active}
          fullUrl={activeFullUrl}
          resolveDownloadUrl={() => annotationsApi.frameUrl(flightId, active.id, "full", teamParam)}
          downloadFilename={frameDownloadName(active, tz)}
          myReviewerId={myReviewerId}
          isAdmin={canManageAll}
          onClose={() => setActiveFrameId(null)}
          onDelete={(id) => { void handleDeleteFrame(id); }}
          onJumpToVideo={(t) => {
            if (active.source === "panorama") {
              jumpToPano(active);
            } else {
              jumpToVideo(t);
            }
          }}
        />
      )}
    </div>
  );
}

// Hide the default <details> disclosure triangle inside FramesPage. Injected
// once at module load so we don't need a separate stylesheet import.
if (typeof document !== "undefined" && !document.getElementById("frames-page-styles")) {
  const style = document.createElement("style");
  style.id = "frames-page-styles";
  style.textContent = `
    summary::-webkit-details-marker { display: none; }
    summary { list-style: none; }
  `;
  document.head.appendChild(style);
}
